"use client";

import { buildEvalHint } from "@health/types/ui-tools";
import { useFocusOnMount } from "@health/ui/hooks/use-focus-on-mount";
import { useEffect, useRef } from "react";
import { useTrack } from "../analytics/useTrack";

type WidgetShellProps = {
  toolName: string;
  toolCallId?: string;
  input: Record<string, unknown>;
  state: "pending" | "submitted";
  className?: string;
  children: React.ReactNode;
  /**
   * When true, focus the shell root on mount with `preventScroll: true`.
   * Pairs with `tabIndex={-1}` so the widget becomes programmatically
   * focusable (SR reads the widget label) without joining the tab order.
   * Only set for read-only widgets that lack a first-interactive-element.
   */
  focusRootOnMount?: boolean;
  /** Accessible label used by SR when `focusRootOnMount` is set. */
  rootAriaLabel?: string;
};

/** Widgets that have no first-interactive-element — focus the root on mount
 *  so keyboard + SR users land on the new widget. These are read-only or
 *  presentational: claims, intake summaries, meducation cards. Widgets with
 *  chips or inputs handle their own focus. */
const ROOT_FOCUS_TOOLS: Record<string, string> = {
  show_claim: "Claim",
  show_intake_summary: "Intake summary",
  show_meducation: "Treatment education",
  show_testimonial: "Testimonial",
  show_patient_profile: "Patient profile",
};

export function isRootFocusWidget(toolName: string): boolean {
  return toolName in ROOT_FOCUS_TOOLS;
}

export function getRootFocusLabel(toolName: string): string | undefined {
  return ROOT_FOCUS_TOOLS[toolName];
}

/**
 * Module-level dedupe set so each (toolCallId, toolName) fires `widget:view`
 * exactly once — survives React StrictMode double-mounts and the active →
 * submitted branch switch in WidgetRegistry.
 */
const VIEW_EMITTED: Set<string> = /* @__PURE__ */ new Set<string>();

/**
 * Wraps every widget with stable test hooks for browser eval automation.
 *
 * - `data-tool`: always present (widget type identifier)
 * - `data-testid`: always present (stable test selector)
 * - `data-tool-state`: always present (pending/submitted)
 * - `data-eval-hint`: only in eval mode (sanitized subset of input, filtered by TOOL_METADATA.evalHintFields)
 */
export function WidgetShell({
  toolName,
  toolCallId,
  input,
  state,
  className,
  children,
  focusRootOnMount,
  rootAriaLabel,
}: WidgetShellProps) {
  const evalHint =
    typeof globalThis !== "undefined" &&
    (globalThis as Record<string, unknown>).__EVAL_MODE__ === true
      ? buildEvalHint(toolName, input)
      : undefined;

  const track = useTrack();
  useEffect(() => {
    if (!toolCallId || state !== "pending") return;
    const key = `${toolCallId}:${toolName}`;
    if (VIEW_EMITTED.has(key)) return;
    VIEW_EMITTED.add(key);
    track("widget:view", { widget_type: toolName, tool_call_id: toolCallId });
  }, [toolCallId, toolName, state, track]);

  const rootRef = useRef<HTMLDivElement>(null);
  // Only fire when the shell is actively presenting a new widget (pending
  // state). Submitted/historical shells should not steal focus.
  useFocusOnMount(rootRef, !!focusRootOnMount && state === "pending");

  return (
    <div
      ref={rootRef}
      data-tool={toolName}
      data-tool-call-id={toolCallId}
      data-testid={`widget-${toolName}`}
      data-tool-state={state}
      data-eval-hint={evalHint ? JSON.stringify(evalHint) : undefined}
      className={className}
      tabIndex={focusRootOnMount ? -1 : undefined}
      role={focusRootOnMount ? "group" : undefined}
      aria-label={focusRootOnMount ? rootAriaLabel : undefined}
    >
      {children}
    </div>
  );
}
